import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { BlogTableOfContents } from "@/components/BlogTableOfContents";
import { BlogReadingProgress } from "@/components/BlogReadingProgress";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, ExternalLink, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ReactNode } from "react";
import { Helmet } from "react-helmet-async";

export interface QuizResource {
  name: string;
  url: string;
  description: string;
}

export interface TocItem {
  id: string;
  title: string;
}

export interface RelatedQuizTopic {
  name: string;
  path: string;
  description: string;
}

interface QuizTopicPageTemplateProps {
  title: string;
  metaTitle: string;
  metaDescription: string;
  keywords: string;
  canonicalUrl: string;
  heroImage?: string;
  heroImageAlt?: string;
  category: string;
  publishDate: string;
  readTime: string;
  tocItems: TocItem[];
  leadParagraph: string;
  children: ReactNode;
  quizResources?: QuizResource[];
  relatedTopics?: RelatedQuizTopic[];
}

export const QuizTopicPageTemplate = ({
  title,
  metaTitle,
  metaDescription,
  keywords,
  canonicalUrl,
  heroImage,
  heroImageAlt,
  category,
  publishDate,
  readTime,
  tocItems,
  leadParagraph,
  children,
  quizResources = [],
  relatedTopics = [],
}: QuizTopicPageTemplateProps) => {
  const baseUrl = "https://csw.live";
  const fullUrl = `${baseUrl}${canonicalUrl}`;
  
  // WebPage Schema
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": metaTitle,
    "description": metaDescription,
    "url": fullUrl,
    "inLanguage": "en-US",
    "datePublished": "2025-01-12",
    "dateModified": "2025-01-12",
    "isPartOf": {
      "@type": "WebSite",
      "name": "CSW - Collaborative Study Workspace",
      "url": baseUrl
    },
    "about": {
      "@type": "Thing",
      "name": title
    },
    "keywords": keywords,
    "mainEntity": {
      "@type": "Article",
      "headline": title,
      "description": leadParagraph,
      "author": {
        "@type": "Organization",
        "name": "CSW",
        "url": baseUrl
      },
      "publisher": {
        "@type": "Organization",
        "name": "CSW",
        "url": baseUrl,
        "logo": {
          "@type": "ImageObject",
          "url": `${baseUrl}/csw-logo.png`
        }
      },
      "datePublished": "2025-01-12",
      "dateModified": "2025-01-12"
    }
  };

  // BreadcrumbList Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": baseUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Quizzes",
        "item": `${baseUrl}/quizzes/`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": title,
        "item": fullUrl
      }
    ]
  };

  // ItemList Schema for Quiz Resources
  const itemListSchema = quizResources.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": `${title} - Quiz Resources`,
    "description": `Curated list of quiz resources for ${title}`,
    "numberOfItems": quizResources.length,
    "itemListElement": quizResources.map((resource, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": resource.name,
      "url": resource.url,
      "description": resource.description
    }))
  } : null;

  // Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "CSW",
    "url": baseUrl,
    "logo": `${baseUrl}/csw-logo.png`,
    "sameAs": [
      "https://twitter.com/cswlive",
      "https://www.youtube.com/@cswlive"
    ]
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO 
        title={metaTitle}
        description={metaDescription}
        keywords={keywords}
        canonicalUrl={canonicalUrl}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        {itemListSchema && (
          <script type="application/ld+json">
            {JSON.stringify(itemListSchema)}
          </script>
        )}
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>
      <Header />
      <BlogReadingProgress />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-secondary/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link 
              to="/quizzes/" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-10 font-body"
              onClick={() => window.scrollTo(0, 0)}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Quizzes
            </Link>
            
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <span className="px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium font-body">
                {category}
              </span>
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-body">
                <Calendar className="w-4 h-4" />
                <span>{publishDate}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-body">
                <Clock className="w-4 h-4" />
                <span>{readTime}</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-10 leading-[1.1] tracking-tight">
              {title}
            </h1>

            {/* Featured Image */}
            {heroImage && (
              <img 
                src={heroImage} 
                alt={heroImageAlt || title} 
                className="w-full aspect-video object-cover rounded-2xl mb-8"
              />
            )}
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="pb-24 relative">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Lead Paragraph */}
            <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed font-body mb-10">
              {leadParagraph}
            </p>

            {/* Table of Contents */}
            <BlogTableOfContents items={tocItems} />

            {/* Main Content */}
            <div className="prose prose-lg prose-invert max-w-none font-body">
              {children}
            </div>

            {/* Quiz Resources Section */}
            {quizResources.length > 0 && (
              <section id="resources" className="mt-16">
                <h2 className="text-3xl font-display font-bold mb-8">
                  <span className="text-gradient">Quiz Resources</span>
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {quizResources.map((resource, index) => (
                    <a 
                      key={index}
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group block p-6 rounded-xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all hover:shadow-lg"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                            {resource.name}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {resource.description}
                          </p>
                        </div>
                        <ExternalLink className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                      </div>
                    </a>
                  ))}
                </div>
              </section>
            )}

            {/* CTA Section */}
            <section className="mt-16 p-8 rounded-2xl bg-gradient-to-br from-primary/10 via-card to-secondary/10 border border-border/50">
              <div className="text-center space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-muted-foreground">Create Your Own Quizzes</span>
                </div>
                <h2 className="text-3xl font-display font-bold">
                  Ready to Test Your Knowledge?
                </h2>
                <p className="text-muted-foreground max-w-xl mx-auto">
                  CSW lets you create custom quizzes with AI, track your progress, and study with friends in real-time.
                </p>
                <Button 
                  size="lg" 
                  className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg"
                  asChild
                >
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                    Start Creating Quizzes
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
              </div>
            </section>

            {/* Related Topics */}
            {relatedTopics.length > 0 && (
              <section className="mt-16">
                <h2 className="text-2xl font-display font-bold mb-6">Related Quiz Topics</h2>
                <div className="grid md:grid-cols-3 gap-4">
                  {relatedTopics.map((topic, index) => (
                    <Link
                      key={index}
                      to={topic.path}
                      onClick={() => window.scrollTo(0, 0)}
                      className="group p-6 rounded-xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all hover:shadow-lg"
                    >
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                        {topic.name}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {topic.description}
                      </p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};
